enum EInterfaceLanguage {
  DUTCH = 'nl',
  ENGLISH = 'en',
  GERMAN = 'de',
  FRENCH = 'fr',
  SPANISH = 'es',
  RUSSIAN = 'ru',
  TURKISH = 'tr',
  /** The language will be based on last known browser-preference  */
  AUTOMATIC = 'automatic',
}

/**
 * A sidebar entry the user pinned to the top of the navigation drawer.
 * Stored as plain data (no component references) so it survives the settings
 * jsonb round-trip; `icon` is a key into the frontend's nav icon registry.
 */
export interface PinnedNavItem {
  /** Single-stack app path, e.g. `/projects/1234`. Doubles as the pin's identity. */
  url: string;
  /** Label snapshot taken when the item was pinned. */
  title: string;
  /** Icon registry key; unknown keys fall back to a generic icon. */
  icon?: string;
}

/**
 * How ticket activity on followed tickets reaches the user by mail.
 * The assignee, executor and newly mentioned users are always mailed directly,
 * whatever this says.
 */
export type TicketNotificationMode = 'direct' | 'digest' | 'both' | 'none';

export const ticketNotificationModes: readonly TicketNotificationMode[] = [
  'direct',
  'digest',
  'both',
  'none',
];

export interface UserSettings {
  /** Mail about other people's changes on followed tickets: per burst (`direct`), daily (`digest`), both, or `none`. Absent = `digest`. */
  ticket_notifications: TicketNotificationMode;
  meetings_enabled: boolean;
  interface_language: EInterfaceLanguage;
  /**
   * Cross-client DEFAULT for the offline service worker opt-in — lets key
   * users get offline sync installed remotely (set via PATCH /me or SQL on
   * the user's settings jsonb). It is only a default: a device where the
   * user explicitly installed or uninstalled (the localStorage choice
   * exists) never follows it — a manual uninstall stays uninstalled.
   */
  offline_sync_default_enabled: boolean;
  /**
   * Sidebar pins, in display order. The first entry doubles as the user's home
   * page: the app root redirects there instead of the default /projects.
   */
  pinned_nav_items: PinnedNavItem[];
}

export type PartialUserSettings = Partial<UserSettings>;

export const defaultUserSettings: UserSettings = {
  ticket_notifications: 'digest',
  interface_language: EInterfaceLanguage.AUTOMATIC,
  meetings_enabled: false,
  offline_sync_default_enabled: false,
  pinned_nav_items: [],
};

export function supplyDefaultUsersettings(
  partialUserSettings: PartialUserSettings,
) {
  return {
    ...defaultUserSettings,
    ...partialUserSettings,
  };
}
